/**
 * Home/Landing page - Extreme Minimalism / Technical Brutalism
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const Home: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6">
      {/* Hero Section - Generous Whitespace */}
      <div className="text-center py-16 md:py-32">
        <div className="flex justify-center mb-8 md:mb-12">
          <img
            src="/logo-primary.svg"
            alt="PermitLoop"
            className="h-16 md:h-24 w-16 md:w-24 hover:ring-pulse cursor-pointer"
          />
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-4 md:mb-6 tracking-tight">
          Stop Waiting Weeks for Permitting Clarity.<br />
        </h1>
        <p className="text-base md:text-xl text-text-secondary mb-8 md:mb-12 max-w-2xl mx-auto px-4">
          Generate comprehensive energy and data center permitting matrices in seconds. Eliminate consultant backlogs and cut project evaluation costs by 95%.
        </p>
        {!user ? (
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link to="/signup" className="btn-primary text-sm md:text-base">
              Get Started Free
            </Link>
            <Link to="/signin" className="btn-secondary text-sm md:text-base">
              Sign In
            </Link>
          </div>
        ) : (
          <Link to="/dashboard" className="btn-primary text-sm md:text-base mx-4">
            Go to Dashboard
          </Link>
        )}
      </div>

      {/* Features - High Density Content, Spacious Layout */}
      <div className="py-12 md:py-24 border-t border-border-dim">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16">
          <div>
            <div className="h-1 w-12 bg-white mb-4 md:mb-6"></div>
            <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">Accuracy without Compromise</h3>
            <p className="text-text-secondary text-sm leading-relaxed">
              Permitting data powered by specialized AI research with human oversight.
            </p>
          </div>
          <div>
            <div className="h-1 w-12 bg-white mb-4 md:mb-6"></div>
            <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">High-Density Output</h3>
            <p className="text-text-secondary text-sm leading-relaxed">
              Permitting matrices with cost and lead time estimates, permitting authorities and reference sources.
            </p>
          </div>
          <div>
            <div className="h-1 w-12 bg-white mb-4 md:mb-6"></div>
            <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">Project Archive</h3>
            <p className="text-text-secondary text-sm leading-relaxed">
              Centralized storage for all permitting matrices.
              Historical references and PDF exports.
            </p>
          </div>
        </div>
      </div>

      {/* Project Types - Built for America's Grid */}
      <div className="py-12 md:py-24 border-t border-border-dim">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">
            Built for the projects reshaping the grid
          </h2>
          <p className="text-text-secondary text-sm md:text-base max-w-3xl mx-auto">
            PermitLoop identifies critical information like the permits you need and who issues them, so you can start your permitting workstream with confidence.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
          {/* Data Centers */}
          <div className="border border-border-dim p-4 md:p-5 transition-all duration-300 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            <div className="font-mono text-text-muted mb-4 text-2xl">01</div>
            <h3 className="text-base md:text-lg font-bold mb-3">Data Centers</h3>
          </div>

          {/* Solar Projects */}
          <div className="border border-border-dim p-4 md:p-5 transition-all duration-300 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            <div className="font-mono text-text-muted mb-4 text-2xl">02</div>
            <h3 className="text-base md:text-lg font-bold mb-3">Solar</h3>
          </div>

          {/* Wind Projects */}
          <div className="border border-border-dim p-4 md:p-5 transition-all duration-300 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            <div className="font-mono text-text-muted mb-4 text-2xl">03</div>
            <h3 className="text-base md:text-lg font-bold mb-3">Wind</h3>
          </div>

          {/* Battery Storage */}
          <div className="border border-border-dim p-4 md:p-5 transition-all duration-300 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            <div className="font-mono text-text-muted mb-4 text-2xl">04</div>
            <h3 className="text-base md:text-lg font-bold mb-3">Battery</h3>
          </div>

          {/* Natural Gas */}
          <div className="border border-border-dim p-4 md:p-5 transition-all duration-300 hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
            <div className="font-mono text-text-muted mb-4 text-2xl">05</div>
            <h3 className="text-base md:text-lg font-bold mb-3">Gas</h3>
          </div>


        </div>
        </div>
      </div>

      {/* CTA - Minimal */}
      <div className="py-12 md:py-24 border-t border-border-dim">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="text-xl md:text-2xl font-bold">Ready to automate permitting matrices?</h2>
          <Link to="/pricing" className="btn-primary text-sm md:text-base whitespace-nowrap">
            View Pricing
          </Link>
        </div>
      </div>
    </div>
  );
};
